import React, { Fragment, useState } from 'react';
import { Typography, IconButton } from '@mui/material';
import format from 'date-fns/format';
import { withStyles } from 'tss-react/mui';
import {
  listsPlugin,
  MDXEditor,
  quotePlugin,
  thematicBreakPlugin,
  headingsPlugin
} from '@mdxeditor/editor';
import EditIcon from '@common/icons/EditIcon';
import FileIcon from '@common/icons/FileIcon';
import { unitResponsesStyles } from './styles';
import { vcmUnitResponseMethodOptions, vcmStatusOptions } from './options';

const MDXPlugins = [quotePlugin(), listsPlugin(), headingsPlugin(), thematicBreakPlugin()];

const CurrentUnitResponse = ({ classes }) => {
  return (
    <Typography className={classes.smallLabel}>
      {vcmUnitResponseMethodOptions.current_unit.label}
    </Typography>
  );
};

const EquivalentUnitResponse = ({ classes, unitResponse }) => {
  return (
    <>
      <Typography className={classes.smallLabel}>
        {vcmUnitResponseMethodOptions.equivalent_unit.label}
      </Typography>
      <div>
        <Typography className={classes.label}>Code:</Typography>
        <Typography className={classes.body2}>
          {unitResponse.equivalent_unit_code}
        </Typography>
      </div>
      <div>
        <Typography className={classes.label}>Title:</Typography>
        <Typography className={classes.body2}>
          {unitResponse.equivalent_unit_title}
        </Typography>
      </div>
    </>
  );
};

const RelevantStudyResponse = ({ classes, unitResponse }) => {
  const [mdxError, setMdxError] = useState(null);

  return (
    <>
      <Typography className={classes.smallLabel}>
        {vcmUnitResponseMethodOptions.relevant_study.label}
      </Typography>
      <div>
        <Typography className={classes.label}>Description:</Typography>
        <Typography className={classes.body2}>
          {unitResponse.study_detail}
        </Typography>
      </div>
      <div>
        <Typography className={classes.label}>Justification:</Typography>
        {mdxError && (
          <Typography className={classes.errorLabel}>Markdown Error: {mdxError}</Typography>
        )}
        <MDXEditor
          plugins={MDXPlugins}
          readOnly
          markdown={unitResponse.study_justification}
          onError={({ error }) => setMdxError(error)}
        />
      </div>
    </>
  );
};

const WorkExperienceResponse = ({ classes, unitResponse }) => {
  const [mdxError, setMdxError] = useState(null);
  const workExperiences = unitResponse.work_experiences || [];
  if (!workExperiences.length) {
    return null;
  }

  return (
    <>
      <Typography className={classes.smallLabel}>
        {vcmUnitResponseMethodOptions.work_experience.label}
      </Typography>
      <div>
        {workExperiences.map((we, index) => (
          <div key={we.id ?? index} className={classes.borderedContainer}>
            <Typography className={classes.smallLabel}>
              {we.employer_name}
            </Typography>
            <Typography color={'textSecondary'} className={classes.body2}>
              {we.job_role}
            </Typography>
            <Typography color={'textSecondary'} className={classes.body2}>
              {we.year_from} - {we.year_to}
            </Typography>
          </div>
        ))}
      </div>

      <div>
        <Typography className={classes.label}>Justification:</Typography>
        {mdxError && (
          <Typography className={classes.errorLabel}>Markdown Error: {mdxError}</Typography>
        )}
        <MDXEditor
          plugins={MDXPlugins}
          readOnly
          markdown={unitResponse.experience_justification}
          onError={({ error }) => setMdxError(error)}
        />
      </div>
    </>
  );
};

const ProblemCheck = ({ classes, unitResponse }) => {
  if (unitResponse.missing_document_problem) {
    return <Typography className={classes.errorLabel}>There is a missing document problem.</Typography>;
  } else if (unitResponse.problem) {
    return <Typography className={classes.errorLabel}>There is a problem.</Typography>;
  } else {
    return null;
  }
}

const SupportingDocuments = ({ classes, supporting_documents }) => {
  const supportingDocuments = supporting_documents || [];
  if (!supportingDocuments.length) {
    return null;
  }

  return (
    <div>
      <Typography className={classes.label}>Supporting documents:</Typography>
      {supportingDocuments.map((doc, index) => (
        <Fragment key={doc.id ?? index}>
          <div className={classes.row}>
            <FileIcon viewBox="0 0 16 16" />
            <div>
              <Typography className={classes.body2}>{doc.title}</Typography>
              <a href={doc.url} target="_blank" rel="noopener noreferrer">
                <Typography className={[classes.body2, classes.link]}>
                  {doc.filename}
                </Typography>
              </a>
            </div>
          </div>
        </Fragment>
      ))}
    </div>
  );
};

const UnitResponseCurrency = ({ classes, currency }) => {
  if (!currency) {
    return null;
  }

  const formattedDate = currency.updated_at
    ? format(new Date(currency.updated_at), 'dd/MM/yy')
    : null;

  switch (currency.status) {
    case 'current':
      return (
        <div>
          <Typography className={classes.label}>Current</Typography>
          {formattedDate ? (
            <Typography className={classes.body2}>{formattedDate}</Typography>
          ) : null}
        </div>
      );
    case 'missing':
      return (
        <Typography className={classes.label}>Missing Currency</Typography>
      );
    case 'outdated':
      return (
        <div>
          <Typography className={classes.label}>Outdated Currency</Typography>
          {formattedDate ? (
            <Typography className={classes.body2}>{formattedDate}</Typography>
          ) : null}
        </div>
      );
    default:
      return null;
  }
};

const EditIconButton = (props) => {
  return (
    <IconButton size="small" sx={{ p: 0 }} {...props}>
      <EditIcon />
    </IconButton>
  );
};

const filterByResponseMethod = (responseMethod) => (doc) =>
  doc.response_method === responseMethod;

const mapSupportingDocument = (doc) => doc.supporting_document;

const VcmUnitResponseDetailCell = ({
  classes,
  row,
  vcm,
  onEdit,
  institute
}) => {
  if (!vcm || row.removed) {
    return null;
  }

  const renderResponseMethod = () => {
    if (row.has_current_unit) {
      return (
        <>
          <CurrentUnitResponse classes={classes} unitResponse={row} />
          <ProblemCheck classes={classes} unitResponse={row} />
          <SupportingDocuments
            classes={classes}
            supporting_documents={row.unit_response_supporting_documents
              ?.filter(filterByResponseMethod('current_unit'))
              ?.map(mapSupportingDocument)}
          />
        </>
      );
    }

    if (row.has_equivalent_unit) {
      return (
        <>
          <EquivalentUnitResponse classes={classes} unitResponse={row} />
          <ProblemCheck classes={classes} unitResponse={row} />
          <SupportingDocuments
            classes={classes}
            supporting_documents={row.unit_response_supporting_documents
              ?.filter(filterByResponseMethod('equivalent_unit'))
              ?.map(mapSupportingDocument)}
          />
        </>
      );
    }

    const response = [];
    if (row.has_relevant_study) {
      response.push(
        <>
          <RelevantStudyResponse classes={classes} unitResponse={row} />
          <ProblemCheck classes={classes} unitResponse={row} />
          <SupportingDocuments
            classes={classes}
            supporting_documents={
              row.unit_response_supporting_documents
                ?.filter(filterByResponseMethod('relevant_study'))
                ?.map(mapSupportingDocument) ?? []
            }
          />
        </>
      );
    }

    if (row.has_work_experience) {
      response.push(
        <>
          <WorkExperienceResponse classes={classes} unitResponse={row} />
          <ProblemCheck classes={classes} unitResponse={row} />
          <SupportingDocuments
            classes={classes}
            supporting_documents={row.unit_response_supporting_documents
              ?.filter(filterByResponseMethod('work_experience'))
              ?.map(mapSupportingDocument)}
          />
        </>
      );
    }

    return response;
  };

  return (
    <div className={classes.container}>
      <div className={classes.cell}>
        {renderResponseMethod()}

        {institute.vocational_currency_uses_units ? (
          <UnitResponseCurrency classes={classes} currency={row.currency} />
        ) : null}
      </div>
      <div>
        <EditIconButton
          onClick={() => {
            onEdit?.(row);
          }}
          disabled={vcm.status !== vcmStatusOptions.in_review.id}
        />
      </div>
    </div>
  );
};

export default withStyles(VcmUnitResponseDetailCell, unitResponsesStyles, {
  withTheme: true
});
